// libraries
import { useEffect, useContext, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useFormContext, useWatch, useFormState } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import Big from 'big.js'
import { Box, Stack, Typography } from '@mui/material'

// Custom components and context imports
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import DatePicker from 'components/generic/form/DatePicker'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import ToAllocateAmountText from '../misc/ToAllocateAmountText'

// Utility and service imports
import { setToAllocateAmount, clearToAllocateAmount } from 'state/invoices'
import { PAYMENT_METHODS_VALUES, ROUNDING_PRECISIONS } from 'utils/constants'
import { useGetCustomerInvoicesQuery, useGetCustomerQuery } from 'services/customers'

// utils + types
import type { InvoicePaymentAction, InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { safeNumber } from 'components/containers/invoice-payments/utils/utils'

const formStyling = {
  padding: '16px',
  width: '666px',
  borderRadius: '8px',
  marginBottom: '16px',
}

interface CreateBulkInvoicePaymentFormProps {
  setOpenInvoices: (invoices: InvoicePaymentCreation[]) => void
  setIsLoadingFormData: (value: boolean) => void
  onRecordPaymentHandler: (data: any) => void
  selectedCustomerId: string | null
  setSelectedCustomerId: (value: string | null) => void
  setSelectedInvoices: (invoices: InvoicePaymentCreation[]) => void
}

const CreateBulkInvoicePaymentForm: React.FC<CreateBulkInvoicePaymentFormProps> = ({
  setOpenInvoices,
  setIsLoadingFormData,
  onRecordPaymentHandler,
  selectedCustomerId,
  setSelectedCustomerId,
  setSelectedInvoices,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { defaultCurrency } = useAppSelector(state => state.settings)
  const toAllocateAmount = useAppSelector(state => state.invoices.toAllocateAmount)

  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const methods = useFormContext()
  const { control, setValue, getValues, handleSubmit, reset } = methods
  const { errors } = useFormState({ control })
  const paymentMethod = useWatch({
    control,
    name: 'paymentMethod',
    defaultValue: PAYMENT_METHODS_VALUES.CHECK.toUpperCase(),
  })
  const checkNumber = useWatch({
    control,
    name: 'checkNumber',
    defaultValue: '',
  })
  const invoicesFormData = useWatch({
    control,
    name: 'invoices',
    defaultValue: {},
  })

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId: selectedCustomerId },
    { refetchOnMountOrArgChange: true, skip: !selectedCustomerId },
  )
  const { data: fetchedOpenInvoices, isFetching: isFetchingOpenInvoices } = useGetCustomerInvoicesQuery(
    { customerId: selectedCustomerId },
    { refetchOnMountOrArgChange: true, skip: !selectedCustomerId },
  )

  useEffect(() => {
    setIsLoadingFormData(isLoadingCustomerData || isFetchingOpenInvoices)
  }, [isLoadingCustomerData, isFetchingOpenInvoices])

  useEffect(() => {
    const totalAllocated: Big = Object.values(invoicesFormData).reduce(
      (sum: Big, invoice: any) => sum.plus(Big(safeNumber(invoice?.amountPaid))).round(ROUNDING_PRECISIONS.COST),
      Big(0),
    )
    const paymentAmountValue = Big(safeNumber(getValues('paymentAmount')))

    const remainingAmount = paymentAmountValue.sub(totalAllocated).round(ROUNDING_PRECISIONS.COST).toNumber()
    if (toAllocateAmount !== remainingAmount) {
      dispatch(setToAllocateAmount(remainingAmount))
    }
  }, [invoicesFormData])

  const updatedActions = useMemo(() => {
    const formValues = getValues()

    const requiredFields = ['consumerName', 'paymentDate', 'paymentMethod', 'paymentAmount']
    if (formValues.paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toUpperCase()) {
      requiredFields.push('checkNumber')
    }

    const areRequiredFieldsFilled = requiredFields.every(field => !!formValues[field])
    const isFullyAllocated = toAllocateAmount === 0

    const isRecordPaymentButtonEnabled = areRequiredFieldsFilled && isFullyAllocated

    const INVOICE_PAYMENT_ACTIONS: InvoicePaymentAction[] = [
      {
        text: 'Record Payment',
        id: 'record-payment-button',
        disabled: !isRecordPaymentButtonEnabled,
        onClick: handleSubmit(onRecordPaymentHandler),
        variant: 'contained',
        'aria-label': 'Record Payment Button',
        'data-testid': 'record-payment-button',
      },
      {
        text: 'Cancel',
        id: 'cancel-invoice-payment-button',
        disabled: false,
        variant: 'text',
        'aria-label': 'Cancel Invoice Payment Button',
        'data-testid': 'cancel-invoice-payment-button',
        onClick: () => navigate('/app/sales/invoice-payments'),
      },
    ]

    return INVOICE_PAYMENT_ACTIONS.map(action => {
      if (action.id === 'record-payment-button') {
        return { ...action, disabled: !isRecordPaymentButtonEnabled }
      }
      return action
    })
  }, [navigate, toAllocateAmount, paymentMethod, checkNumber])

  useEffect(() => {
    contentContext.setContainerActions(updatedActions)
  }, [updatedActions])

  useEffect(() => {
    if (selectedCustomerId && customerData) {
      const currency = customerData?.defaultCurrency || defaultCurrency
      setValue('currency', currency)
    }
  }, [selectedCustomerId, customerData, setValue, defaultCurrency])

  useEffect(() => {
    contentContext.setContainerTitle('New Invoice Payment')
    tabsContext?.setContainerTabsVisibility(false)
  }, [])

  useEffect(() => {
    if (selectedCustomerId && fetchedOpenInvoices) {
      const sortedInvoices = [...fetchedOpenInvoices].sort((a, b) =>
        a.invoiceTicketId.localeCompare(b.invoiceTicketId, undefined, { numeric: true, sensitivity: 'base' }),
      )
      setOpenInvoices(sortedInvoices)
    }
  }, [selectedCustomerId, fetchedOpenInvoices])

  return (
    <Box p={3} pb={0} data-testid="payment-information-box">
      <Typography variant="h6" gutterBottom data-testid="payment-information-title">
        Payment Information
      </Typography>
      <Stack spacing={2} sx={formStyling} data-testid="payment-form-stack">
        <Stack direction="row" spacing={2} data-testid="consumer-name-payment-date-stack">
          <CustomerAutocomplete
            name="consumerName"
            label="Consumer Name"
            variant="outlined"
            size="medium"
            defaultValue={null}
            freeSolo={false}
            onChange={customer => {
              if (customer) {
                setSelectedCustomerId(customer.id)
                setValue('invoices', {})
                if (customer.preferredPaymentMethod) {
                  setValue('paymentMethod', customer.preferredPaymentMethod.toUpperCase())
                }
              } else {
                setSelectedCustomerId(null)
                dispatch(clearToAllocateAmount())
                reset({
                  consumerName: null,
                  paymentDate: new Date(),
                  paymentMethod: PAYMENT_METHODS_VALUES.CHECK.toUpperCase(),
                  paymentAmount: '',
                  checkNumber: '',
                  paymentNote: '',
                  invoices: {},
                })
              }
              setSelectedInvoices([])
              setOpenInvoices([])
            }}
            control={control}
            autocompleteProps={{
              renderOption: (props, option) => (
                <li {...props} key={option.id}>
                  {option.name || option.displayName}
                </li>
              ),
            }}
            error={!!errors.consumerName}
            helperText={errors.consumerName?.message}
            aria-label="Consumer Name Input"
            data-testid="consumer-name-input"
          />
          <DatePicker
            name="paymentDate"
            label="Payment Date"
            control={control}
            variant="outlined"
            defaultDate={new Date()}
            error={!!errors.paymentDate}
            helperText={errors.paymentDate?.message}
            InputLabelProps={{ shrink: true }}
            aria-label="Payment Date Picker"
            data-testid="payment-date-picker"
          />
        </Stack>
        <Stack direction="row" spacing={2} data-testid="payment-method-check-number-stack">
          <Select
            name="paymentMethod"
            label="Payment Method"
            defaultValue={PAYMENT_METHODS_VALUES.CHECK.toUpperCase()}
            control={control}
            items={[
              { id: PAYMENT_METHODS_VALUES.CHECK.toUpperCase(), name: PAYMENT_METHODS_VALUES.CHECK },
              { id: PAYMENT_METHODS_VALUES.CASH.toUpperCase(), name: PAYMENT_METHODS_VALUES.CASH },
              { id: PAYMENT_METHODS_VALUES.EFT.toUpperCase(), name: PAYMENT_METHODS_VALUES.EFT },
            ]}
            error={!!errors.paymentMethod}
            helperText={errors.paymentMethod?.message}
            selectProps={{
              onChange: ({ target: { value } }) => {
                setValue('paymentMethod', value)
                if (value !== PAYMENT_METHODS_VALUES.CHECK.toUpperCase()) {
                  setValue('checkNumber', '')
                }
              },
            }}
            data-testid="payment-method-input"
            InputLabelProps={{ shrink: true }}
          />
          {paymentMethod === PAYMENT_METHODS_VALUES.CHECK.toUpperCase() && (
            <Input
              name="checkNumber"
              label="Check #"
              control={control}
              variant="outlined"
              fullWidth
              error={!!errors.checkNumber}
              helperText={errors.checkNumber?.message}
              data-testid="check-number-input"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ width: '100%' }} data-testid="payment-amount-to-allocate-stack">
          <Box sx={{ width: '50%' }}>
            <Input
              name="paymentAmount"
              label="Payment Amount"
              defaultValue=""
              control={control}
              id="payment-amount"
              fullWidth
              variant="outlined"
              InputProps={{
                inputComponent: InputFormat,
                inputProps: {
                  inputType: 'price',
                  decimalScale: 2,
                  allowNegative: false,
                  'data-testid': 'payment-amount-input',
                  'aria-label': 'Payment Amount Input',
                },
              }}
              error={!!errors.paymentAmount}
              helperText={errors.paymentAmount?.message}
              onChange={event => {
                const paymentAmount = Big(safeNumber(event?.target?.value))
                const totalAllocated: Big = Object.values(invoicesFormData).reduce(
                  (sum: Big, invoice: any) =>
                    sum.plus(Big(safeNumber(invoice?.amountPaid))).round(ROUNDING_PRECISIONS.COST),
                  Big(0),
                )
                const remainingAmount = paymentAmount.sub(totalAllocated).round(ROUNDING_PRECISIONS.COST).toNumber()
                dispatch(setToAllocateAmount(remainingAmount))
              }}
              fractionDigits={2}
            />
          </Box>
          <ToAllocateAmountText />
        </Stack>
      </Stack>
    </Box>
  )
}

export default CreateBulkInvoicePaymentForm
